/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	serializeIDTToYAML,
	TBlueprintIDTNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventListener,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { BlueprintTheme, TBlueprintThemeSchema } from "@adapptio/lib-core";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { ThemeEditSelection } from "./ThemeEditSelection";
import { Debounce } from "../../../decorators";

export class ThemeModel {
	private readonly dCtx: DesignContext;

	public readonly onModelChange = createEventEmitter<void>();

	public readonly undoRedoManager = new UndoRedoManager({ historyLimit: 10 });

	private readonly selection = new ThemeEditSelection();

	private model: TGetBlueprintSchemaModel<TBlueprintThemeSchema> = null;

	private lastSnapshot: TBlueprintIDTNode | null = null;

	constructor(private runtimeSvc: RuntimeService) {
		this.dCtx = new DesignContext({
			resolvers: this.runtimeSvc.getBaseResolvers(),
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		onEvent(this.dCtx.modelChangeEvent, this.handleModelChange);
	}

	public dispose(): void {
		removeAllEventListeners(this.onModelChange);

		offEvent(this.dCtx?.modelChangeEvent, this.handleModelChange);

		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		this.dCtx.destroy();
	}

	private handleModelChange: TGetSimpleEventListener<DesignContext["modelChangeEvent"]> = (eventData) => {
		console.log("Model has changed", eventData);
		this.checkSelection();
		this.createSnapshot();
		emitEvent(this.onModelChange);
	};

	public async createDefault(): Promise<void> {
		this.model = BlueprintTheme.createDefault(this.dCtx, null);

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	private async initModel(): Promise<void> {
		this.handleModelChange();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.model = this.dCtx.loadYAMLDocument(BlueprintTheme, blueprintContent, "local://theme");

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	private serializeModelToIdt(): TBlueprintIDTNode {
		return this.model.schema.serialize(this.model, [ "$" ]);
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.serializeModelToIdt();

		return serializeIDTToYAML(idt);
	}

	private applySnapshotToModel(snapshot: TBlueprintIDTNode): void {
		this.model = BlueprintTheme.parse(this.dCtx, snapshot);
		this.initModel();
		this.lastSnapshot = snapshot;
		this.selection.deselectAll();
	}

	@Debounce(500)
	private createSnapshot(): void {
		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.serializeModelToIdt();
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.applySnapshotToModel(prevSnapshot),
				redo: () => this.applySnapshotToModel(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});

			emitEvent(this.onModelChange);
		}
	}

	private checkSelection() {
		const items = this.selection.getItems().slice();

		for (let i = 0; i < items.length; i++) {
			if (!this.dCtx.getNodeById(items[i].nodeId)) {
				this.selection.deselectByNodeId(items[i].nodeId);
			}
		}
	}

	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	public getModel(): TGetBlueprintSchemaModel<TBlueprintThemeSchema> {
		return this.model;
	}

	public getSelection(): ThemeEditSelection {
		return this.selection;
	}
}
